import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import getConfig from 'next/config';
import styled from 'styled-components';
import { Builder } from '@builder.io/react/lite';
import { addIdAttribute } from '@glowforge/builder-components/lib/util/shared-configs';
import { observeAuthHandoff } from '../../util/utm-observability';

const { publicRuntimeConfig } = getConfig();
const { GF_DOTCOM_GOOGLE_CLIENT_ID, GF_DOTCOM_USER_SERVICE_ENDPOINT } = publicRuntimeConfig;

const GOOGLE_SCRIPT_ID = 'google-gsi-client';
const COOKIE_DOMAIN = '.glowforge.com';

const ButtonWrapper = styled.div`
  position: relative;
  width: 100%;

  &:hover .google-signin-button {
    background-color: #f8f9fa;
    border-color: #c6c6c6;
  }
`;

const GoogleButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  padding: 14px 24px;
  border: 1px solid #dadce0;
  border-radius: 100px;
  background-color: #fff;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #3c4043;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;
  pointer-events: none;
`;

const GoogleButtonOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  opacity: 0.0001;
  cursor: pointer;

  & iframe {
    width: 100% !important;
    height: 100% !important;
  }
`;

// Google "G" logo SVG - paths are long by nature, disable max-len
/* eslint-disable max-len */
function GoogleLogo() {
  return (
    <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path d='M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z' fill='#4285F4' />
      <path d='M9.003 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.836.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332A8.997 8.997 0 0 0 9.003 18z' fill='#34A853' />
      <path d='M3.964 10.712A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.96A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.96 4.042l3.004-2.33z' fill='#FBBC05' />
      <path d='M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.002 0A8.997 8.997 0 0 0 .96 4.958l3.004 2.332c.708-2.127 2.692-3.71 5.036-3.71z' fill='#EA4335' />
    </svg>
  );
}
/* eslint-enable max-len */

/**
 * Submit the Google credential to user-service via a hidden form POST.
 * Sets a g_csrf_token cookie on .glowforge.com so user-service's CSRF
 * validation passes (it checks cookie === form field).
 */
export function submitCredentialToUserService(credential, { allowCreate, redirectUrl }) {
  try {
    observeAuthHandoff('google');
  } catch {
    // Observability must never block authentication.
  }
  const csrfToken = crypto.randomUUID();

  // Set g_csrf_token cookie on shared domain so user-service can read it
  const cookieParts = [
    `g_csrf_token=${csrfToken}`,
    `domain=${COOKIE_DOMAIN}`,
    'path=/', 'secure', 'samesite=lax',
  ];
  document.cookie = cookieParts.join('; ');

  const form = document.createElement('form');
  form.method = 'POST';

  const baseUri = `${GF_DOTCOM_USER_SERVICE_ENDPOINT}/users/sign_in`;
  const params = new URLSearchParams();
  if (redirectUrl) {
    params.append('redirect_url', redirectUrl);
  } else {
    params.append('redirect_url', window.location.href);
  }
  const qs = params.toString();
  form.action = qs ? `${baseUri}?${qs}` : baseUri;

  const fields = { credential, g_csrf_token: csrfToken };
  if (allowCreate) {
    fields.allow_create = 'true';
  }

  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
}

function GoogleSignIn({
  attributes,
  dataTestId,
  allowCreate,
  redirectUrl,
}) {
  const overlayRef = useRef(null);
  const isInitializedRef = useRef(false);
  const propsRef = useRef({ allowCreate, redirectUrl });
  propsRef.current = { allowCreate, redirectUrl };

  const handleCredentialResponse = useCallback((response) => {
    submitCredentialToUserService(response.credential, propsRef.current);
  }, []);

  useEffect(() => {
    // Don't load in Builder editor
    if (Builder.isEditing) return undefined;

    // Check if script already exists
    let script = document.getElementById(GOOGLE_SCRIPT_ID);

    if (!script) {
      script = document.createElement('script');
      script.id = GOOGLE_SCRIPT_ID;
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }

    const initializeGsi = () => {
      if (
        window.google?.accounts?.id
        && overlayRef.current
        && !isInitializedRef.current
      ) {
        isInitializedRef.current = true;

        // Popup mode returns the credential via JS callback
        // instead of redirecting, avoiding redirect_uri registration.
        window.google.accounts.id.initialize({
          client_id: GF_DOTCOM_GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          ux_mode: 'popup',
        });

        // Render the real Google button into the transparent overlay.
        // User clicks it directly (opens full account chooser popup),
        // credential returns via callback, then we form-POST to
        // user-service with all needed params.
        window.google.accounts.id.renderButton(
          overlayRef.current,
          {
            theme: 'outline',
            size: 'large',
            width: overlayRef.current.offsetWidth,
          }
        );
      }
    };

    if (script.loaded || window.google?.accounts?.id) {
      initializeGsi();
    } else {
      script.addEventListener('load', () => {
        script.loaded = true;
        initializeGsi();
      });
    }

    return undefined;
  }, [handleCredentialResponse]);

  // Show placeholder in Builder editor
  if (Builder.isEditing) {
    return (
      <div {...addIdAttribute(dataTestId)} {...attributes}>
        <GoogleButton as='button' disabled className='google-signin-button'>
          <GoogleLogo />
          Continue with Google
          <span style={{ fontSize: '11px', color: '#999', marginLeft: '4px' }}>
            (preview)
          </span>
        </GoogleButton>
      </div>
    );
  }

  return (
    <div {...addIdAttribute(dataTestId)} {...attributes}>
      <ButtonWrapper>
        <GoogleButton className='google-signin-button'>
          <GoogleLogo />
          Continue with Google
        </GoogleButton>
        <GoogleButtonOverlay ref={overlayRef} />
      </ButtonWrapper>
    </div>
  );
}

GoogleSignIn.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  attributes: PropTypes.object,
  dataTestId: PropTypes.string,
  allowCreate: PropTypes.bool,
  redirectUrl: PropTypes.string,
};

GoogleSignIn.defaultProps = {
  attributes: {},
  dataTestId: null,
  allowCreate: false,
  redirectUrl: '',
};

export default GoogleSignIn;
